/**
 * Event Detail Page
 *
 * Complete detail view for a single event record with 5 tabs
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedEventRecords } from '../../services/mockData/events';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { FilesTab } from '../../components/detail/FilesTab';
import { HistoryTab } from '../../components/detail/HistoryTab';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { EventModal } from '../../components/modals/EventModal';
import { useToast } from '../../contexts/ToastContext';
import { isFeatureEnabled } from '../../config/mvpConfig';
import { Users, StickyNote } from 'lucide-react';
interface EventDetailPageProps {
  id: string;
  onBack: () => void;
}

export const EventDetailPage: React.FC<EventDetailPageProps> = ({ id, onBack }) => {
  const { success, info } = useToast();
  const queryClient = useQueryClient();
  const allTabs = usePageLayoutTabs('Event__c');
  const tabs = allTabs.filter((t: { id: string }) => t.id !== 'files' || isFeatureEnabled('showFilesTab'));
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Event', id);
        if (!record) throw new Error('Event not found');
        return { ...record, subject: record.subject ?? (record as any).Subject__c ?? record.label };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allEvents = persistedEventRecords.get();
      const found = allEvents.find(e => e.id === id);
      if (!found) throw new Error('Event not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Event__c', event);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displaySubject = event?.subject ?? (event as any)?.Subject__c ?? event?.label ?? 'this event';
    if (!window.confirm(`Are you sure you want to delete "${displaySubject}"?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Event__c', id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['eventListViewData'] });
          queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Event__c'] });
          success('Event deleted', `"${displaySubject}" has been deleted`);
          onBack();
        })
        .catch((err) => {
          console.error('[EventDetailPage] Delete failed:', err);
          success('Error', `Failed to delete event: ${err?.message || 'Unknown error'}`);
        });
      return;
    }
    const allEvents = persistedEventRecords.get();
    persistedEventRecords.set(allEvents.filter(e => e.id !== id));
    success('Event deleted', `"${displaySubject}" has been deleted`);
    onBack();
  };

  // Format date nicely: "Feb 17, 2025 at 9:00 AM"
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate duration: "1 hour" or "2 hours 30 minutes"
  const formatDuration = (minutes: number) => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins} minute${mins !== 1 ? 's' : ''}`;
    } else if (mins === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    }
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Events">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!event) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Events">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Event not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    'Confirmed': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Scheduled': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'Tentative': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Completed': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300',
    'Cancelled': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  };

  // Event type badge colors
  const typeColors: Record<string, string> = {
    'Meeting': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'Call': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Email': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    'Demo': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    'Task': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    'Other': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isAttendees = ['attendees'].includes(activeTab);
  const isNotes = ['notes', 'tab_004'].includes(activeTab);
  const isFiles = ['files'].includes(activeTab);
  const isHistory = ['history'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Events">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={event.subject as string}
          subtitle={`${event.type} • ${formatDateTime(event.startDateTime as string)}`}
          recordId={event.id as string}
          status={event.status as string}
          statusColor={statusColors[event.status as string] || statusColors.Scheduled}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Event Type Badge */}
        {event.type && (
          <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${typeColors[event.type as string] || typeColors.Other}`}>
              {event.type}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Event"
                      record={event}
                    />
                  ))
                )}
              </div>
            )}

            {isAttendees && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No attendees yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Event attendees and participants will appear here.
                  </p>
                </div>
              </div>
            )}

            {isNotes && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <StickyNote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No notes yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Event notes will appear here.
                  </p>
                </div>
              </div>
            )}

            {isFiles && isFeatureEnabled('showFilesTab') && <FilesTab />}
            {isHistory && <HistoryTab recordId={id} objectType="Event__c" />}
          </div>
        </div>
      </div>

      {/* Event Edit Modal */}
      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={event}
      />
    </DetailPageLayout>
  );
};
